import type { HTMLAttributes, ReactNode, SVGAttributes } from "react";
import type {
  TLHandle,
  TLParentId,
  TLShape,
  TLShapeId,
  TLShapePartial,
} from "@tldraw/tlschema";
import type { IndexKey } from "@tldraw/utils";
import { Vec, type BoxModel, type VecLike } from "../geometry";
import type { PlanroomEditor } from "../editor";
import { Rectangle2d, type Geometry2d } from "./geometry2d";

// Shape behaviour for Planroom's engine. The hook names match those Planroom's
// shape classes already implement, so they move over by changing their base.

export type ResizeHandle =
  | "top"
  | "right"
  | "bottom"
  | "left"
  | "top_left"
  | "top_right"
  | "bottom_right"
  | "bottom_left";
export type ResizeInfo<S extends TLShape = TLShape> = {
  /** The shape's new position in its parent's space. */
  newPoint: Vec;
  handle: ResizeHandle;
  mode: "scale_shape" | "resize_bounds";
  scaleX: number;
  scaleY: number;
  initialBounds: BoxModel;
  initialShape: S;
};
export type HandleDragInfo<S extends TLShape = TLShape> = {
  handle: TLHandle;
  isPrecise: boolean;
  initial?: S;
};
/** Where dragged shapes started, for containers deciding how to adopt or release them. */
export type DragShapesInfo = {
  initialParentIds: Map<TLShapeId, TLParentId>;
  initialIndices: Map<TLShapeId, IndexKey>;
  initialDraggingOverShapeId: TLShapeId | null;
  prevDraggingOverShapeId: TLShapeId | null;
  nextDraggingOverShapeId: TLShapeId | null;
};
export type SvgExportContext = {
  addExportDef?(def: unknown): void;
  isDarkMode?: boolean;
};

/** Any class that can act as a shape's behaviour in this engine. */
export interface ShapeUtilLike<S extends TLShape = TLShape> {
  getGeometry(shape: S): Geometry2d | { bounds: BoxModel };
  component(shape: S): ReactNode;
  [hook: string]: any;
}
export type ShapeUtilConstructor = {
  new (editor: any): ShapeUtilLike<any>;
  type: string;
  props?: unknown;
  migrations?: unknown;
};

export abstract class ShapeUtil<S extends TLShape = TLShape> {
  static type: string;
  static props?: unknown;
  static migrations?: unknown;
  constructor(public editor: PlanroomEditor) {}

  abstract getDefaultProps(): S["props"];
  abstract getGeometry(shape: S): Geometry2d;
  abstract component(shape: S): ReactNode;

  /** Outline drawn for selection and hover, in shape space. */
  getIndicatorPath(shape: S): string | Path2D | undefined {
    const g = this.getGeometry(shape);
    return g.toSvgPath();
  }
  toSvg?(shape: S, ctx: SvgExportContext): ReactNode | Promise<ReactNode>;

  canResize(_shape: S) {
    return true;
  }
  isAspectRatioLocked(_shape: S) {
    return false;
  }
  hideRotateHandle(_shape: S) {
    return false;
  }
  hideResizeHandles(_shape: S) {
    return false;
  }
  hideSelectionBoundsBg(_shape: S) {
    return false;
  }
  hideSelectionBoundsFg(_shape: S) {
    return false;
  }
  canEdit(_shape: S) {
    return false;
  }
  canScroll(_shape: S) {
    return false;
  }
  canBind(_opts?: unknown) {
    return true;
  }
  canCrop(_shape: S) {
    return false;
  }
  canReceiveNewChildrenOfType(_shape: S, _type: TLShape["type"]) {
    return false;
  }
  canRemoveChildrenOfType(_shape: S, _type: TLShape["type"]) {
    return true;
  }
  canResizeChildren(_shape: S) {
    return true;
  }
  shouldClipChild(_child: TLShape) {
    return false;
  }
  /** Clip polygon for children, in this shape's space. */
  getClipPath(_shape: S): VecLike[] | undefined {
    return undefined;
  }
  getHandles?(shape: S): TLHandle[];
  getText?(shape: S): string | undefined;
  onResize?(shape: S, info: ResizeInfo<S>): TLShapePartial<S> | void;
  onBeforeCreate?(next: S): S | void;
  onBeforeUpdate?(prev: S, next: S): S | void;
  onHandleDragStart?(
    shape: S,
    info: HandleDragInfo<S>,
  ): TLShapePartial<S> | void;
  onHandleDrag?(shape: S, info: HandleDragInfo<S>): TLShapePartial<S> | void;
  onHandleDragEnd?(shape: S, info: HandleDragInfo<S>): TLShapePartial<S> | void;
  onHandleDragCancel?(shape: S, info: HandleDragInfo<S>): void;
  onTranslateStart?(shape: S): TLShapePartial<S> | void;
  /** Drawn beside the shape (not clipped by it) while the shape is being cropped. */
  cropPreview?(shape: S): ReactNode;
  onDragShapesIn?(shape: S, shapes: TLShape[], info: DragShapesInfo): void;
  onDragShapesOut?(shape: S, shapes: TLShape[], info: DragShapesInfo): void;
  onDoubleClick?(shape: S): TLShapePartial<S> | void;
}

type BoxShape = TLShape & { props: { w: number; h: number } };

export abstract class BaseBoxShapeUtil<
  S extends BoxShape,
> extends ShapeUtil<S> {
  getGeometry(shape: S): Geometry2d {
    return new Rectangle2d({
      width: shape.props.w,
      height: shape.props.h,
      isFilled: true,
    });
  }
  onResize(shape: S, info: ResizeInfo<S>) {
    return resizeBox(shape, info);
  }
}

/** Containers (sheets, tracing paper) that own child shapes. */
export abstract class BaseFrameLikeShapeUtil<
  S extends BoxShape,
> extends BaseBoxShapeUtil<S> {
  static isFrameLike = true;
  override getGeometry(shape: S): Geometry2d {
    return new Rectangle2d({
      width: shape.props.w,
      height: shape.props.h,
      isFilled: false,
    });
  }
  override canReceiveNewChildrenOfType(shape: S, _type: TLShape["type"]) {
    return !shape.isLocked;
  }
  override shouldClipChild(_child: TLShape) {
    return true;
  }
  override getClipPath(shape: S): VecLike[] | undefined {
    return this.getGeometry(shape).vertices;
  }
}

/** Resize a w/h box shape to the engine's target bounds, within limits. */
export function resizeBox<S extends BoxShape>(
  shape: S,
  info: ResizeInfo<S>,
  limits: {
    minWidth?: number;
    maxWidth?: number;
    minHeight?: number;
    maxHeight?: number;
  } = {},
): TLShapePartial<S> {
  const { initialShape, scaleX, scaleY, newPoint, handle } = info;
  const clamp = (v: number, min = 1, max = Infinity) =>
    Math.min(max, Math.max(min, v));
  const targetW = initialShape.props.w * Math.abs(scaleX),
    targetH = initialShape.props.h * Math.abs(scaleY);
  const w = clamp(targetW, limits.minWidth ?? 1, limits.maxWidth),
    h = clamp(targetH, limits.minHeight ?? 1, limits.maxHeight);
  // Keep the edge opposite the dragged handle fixed when a limit applies.
  const offset = new Vec(
    handle.includes("left") ? targetW - w : 0,
    handle.includes("top") ? targetH - h : 0,
  );
  const rotated =
    offset.x || offset.y ? rotate(offset, shape.rotation) : offset;
  return {
    id: shape.id,
    type: shape.type,
    x: newPoint.x + rotated.x,
    y: newPoint.y + rotated.y,
    props: { w, h },
  } as unknown as TLShapePartial<S>;
}
const rotate = (v: Vec, angle: number) =>
  new Vec(
    v.x * Math.cos(angle) - v.y * Math.sin(angle),
    v.x * Math.sin(angle) + v.y * Math.cos(angle),
  );

export function HTMLContainer({
  children,
  className,
  style,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { children?: ReactNode }) {
  return (
    <div
      {...rest}
      className={["canvas-html-container", className].filter(Boolean).join(" ")}
      style={style}
    >
      {children}
    </div>
  );
}

export function SVGContainer({
  children,
  className,
  style,
  ...rest
}: SVGAttributes<SVGSVGElement> & { children?: ReactNode }) {
  return (
    <svg
      {...rest}
      aria-hidden="true"
      className={["canvas-svg-container", className].filter(Boolean).join(" ")}
      style={style}
    >
      {children}
    </svg>
  );
}
